import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { PlatformLayout } from "./platform-layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TiersContent } from "./platform-tiers";
import { CouponsContent } from "./platform-coupons";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, RotateCw, Plus, Coins } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface LeadCreditTier {
  id: string;
  name: string;
  displayName: string | null;
  monthlyLeadCredits: number;
}

interface LeadCreditConfig {
  pricePerCreditCents: number;
  packs: Array<{ priceCents: number; credits: number; label: string }>;
  tiers: LeadCreditTier[];
}

function LeadCreditsContent() {
  const { toast } = useToast();
  const { data: leadCreditConfig } = useQuery<LeadCreditConfig>({
    queryKey: ["/api/platform/lead-credits/config"],
  });
  const [tierGrantInputs, setTierGrantInputs] = useState<Record<string, string>>({});
  const [awardWorkspaceId, setAwardWorkspaceId] = useState("");
  const [awardCreditsAmount, setAwardCreditsAmount] = useState("");
  const [awardNotes, setAwardNotes] = useState("");

  const updateTierGrantMutation = useMutation({
    mutationFn: async (vars: { tierId: string; monthlyLeadCredits: number }) =>
      apiRequest("PATCH", `/api/platform/lead-credits/tier/${vars.tierId}`, {
        monthlyLeadCredits: vars.monthlyLeadCredits,
      }),
    onSuccess: () => {
      toast({ title: "Tier updated", description: "New monthly grant applies on the next reset." });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/lead-credits/config"] });
    },
    onError: (err: any) =>
      toast({ title: "Update failed", description: err?.message, variant: "destructive" }),
  });

  const awardCreditsMutation = useMutation({
    mutationFn: async () =>
      apiRequest("POST", "/api/platform/lead-credits/award", {
        workspaceId: awardWorkspaceId.trim(),
        credits: parseInt(awardCreditsAmount, 10),
        notes: awardNotes.trim() || undefined,
      }),
    onSuccess: () => {
      toast({ title: "Credits awarded", description: `${awardCreditsAmount} credits added to workspace.` });
      setAwardWorkspaceId("");
      setAwardCreditsAmount("");
      setAwardNotes("");
    },
    onError: (err: any) =>
      toast({ title: "Award failed", description: err?.message, variant: "destructive" }),
  });

  const resetGrantsMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/platform/lead-credits/reset-grants", {});
      return res.json();
    },
    onSuccess: (data: any) =>
      toast({ title: "Reset complete", description: `${data.count} workspaces reset.` }),
    onError: (err: any) =>
      toast({ title: "Reset failed", description: err?.message, variant: "destructive" }),
  });

  return (
    <div className="space-y-4">
      <Card data-testid="card-lead-credits-settings">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Coins className="h-5 w-5 text-amber-500" />
            Lead Generation Credits
          </CardTitle>
          <CardDescription>
            1 credit = 1 imported lead. Set monthly grants per tier (use-it-or-lose-it).
            Purchased credits roll over indefinitely.
            {leadCreditConfig && (
              <span className="block mt-1">
                Price per credit:{" "}
                <strong>${(leadCreditConfig.pricePerCreditCents / 100).toFixed(2)}</strong>
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <h3 className="text-sm font-semibold mb-2">Monthly grant by tier</h3>
            <div className="space-y-2">
              {leadCreditConfig?.tiers.map((tier) => (
                <div key={tier.id} className="flex items-center gap-3">
                  <div className="w-32 text-sm font-medium capitalize">
                    {tier.displayName || tier.name}
                  </div>
                  <Input
                    type="number"
                    min={0}
                    defaultValue={tier.monthlyLeadCredits}
                    onChange={(e) =>
                      setTierGrantInputs((p) => ({ ...p, [tier.id]: e.target.value }))
                    }
                    className="w-32"
                    data-testid={`input-tier-grant-${tier.name}`}
                  />
                  <span className="text-xs text-muted-foreground">credits/month</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const v = tierGrantInputs[tier.id];
                      if (v === undefined) return;
                      const n = parseInt(v, 10);
                      if (!Number.isFinite(n) || n < 0) {
                        toast({ title: "Invalid value", variant: "destructive" });
                        return;
                      }
                      updateTierGrantMutation.mutate({
                        tierId: tier.id,
                        monthlyLeadCredits: n,
                      });
                    }}
                    disabled={
                      tierGrantInputs[tier.id] === undefined ||
                      updateTierGrantMutation.isPending
                    }
                    data-testid={`button-save-tier-grant-${tier.name}`}
                  >
                    Save
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-4 border-t">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Award credits to a workspace
            </h3>
            <p className="text-xs text-muted-foreground mb-3">
              Awarded credits are added to the workspace's purchased balance (rolls over,
              never resets).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Workspace ID</Label>
                <Input
                  value={awardWorkspaceId}
                  onChange={(e) => setAwardWorkspaceId(e.target.value)}
                  placeholder="ws_..."
                  data-testid="input-award-workspace-id"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Credits</Label>
                <Input
                  type="number"
                  min={1}
                  value={awardCreditsAmount}
                  onChange={(e) => setAwardCreditsAmount(e.target.value)}
                  placeholder="100"
                  data-testid="input-award-credits"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Notes (optional)</Label>
                <Input
                  value={awardNotes}
                  onChange={(e) => setAwardNotes(e.target.value)}
                  placeholder="Promo / make-good / ..."
                  data-testid="input-award-notes"
                />
              </div>
            </div>
            <Button
              className="mt-3"
              size="sm"
              onClick={() => awardCreditsMutation.mutate()}
              disabled={
                !awardWorkspaceId.trim() ||
                !awardCreditsAmount ||
                awardCreditsMutation.isPending
              }
              data-testid="button-award-credits"
            >
              {awardCreditsMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Award credits
            </Button>
          </div>

          <div className="pt-4 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (
                  window.confirm(
                    "Reset monthly grants for ALL workspaces now? This is normally automatic on the 1st."
                  )
                ) {
                  resetGrantsMutation.mutate();
                }
              }}
              disabled={resetGrantsMutation.isPending}
              data-testid="button-reset-grants-now"
            >
              {resetGrantsMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RotateCw className="h-4 w-4 mr-2" />
              )}
              Reset monthly grants now (manual)
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              Auto-runs at 04:00 UTC on the 1st of each month. Resets only the grant
              bucket; purchased credits unaffected.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function PlansHubContent() {
  return (
    <Tabs defaultValue="tiers">
      <TabsList className="flex flex-wrap h-auto gap-1 mb-4">
        <TabsTrigger value="tiers" data-testid="tab-plans-tiers">
          Tiers
        </TabsTrigger>
        <TabsTrigger value="coupons" data-testid="tab-plans-coupons">
          Coupons
        </TabsTrigger>
        <TabsTrigger value="lead-credits" data-testid="tab-plans-lead-credits">
          <Coins className="h-4 w-4 mr-1.5" />
          Lead Credits
        </TabsTrigger>
      </TabsList>

      <TabsContent value="tiers">
        <TiersContent />
      </TabsContent>
      <TabsContent value="coupons">
        <CouponsContent />
      </TabsContent>
      <TabsContent value="lead-credits">
        <LeadCreditsContent />
      </TabsContent>
    </Tabs>
  );
}

export default function PlatformPlansHub() {
  return (
    <PlatformLayout>
      <PlansHubContent />
    </PlatformLayout>
  );
}
